/**
 * Stop event handler for draggable node views.
 *
 * This handler allows drag, clipboard, keyboard, and composition/IME events to propagate
 * to ProseMirror while stopping other events that could interfere with the
 * node view's behavior. Keyboard events are needed so that Delete/Backspace can
 * remove selected nodes. Composition events are needed for emoji pickers and
 * input method editors within contenteditable areas inside node views.
 *
 * Events originating from form input elements (input, textarea, select) are always
 * stopped to prevent ProseMirror from intercepting clipboard/keyboard actions
 * meant for those fields (e.g. pasting into a label name input).
 *
 * Use this with `stopEvent` option in `ReactNodeViewRenderer` or `AngularNodeViewRenderer`
 * for draggable atom nodes that use custom node views.
 */
export function draggableNodeStopEvent({ event }: { event: Event }): boolean {
  // Events from form input elements should never reach ProseMirror.
  // This prevents paste/keyboard events in inputs within the node view
  // from being processed by the editor.
  if (isFormInputElement(event.target)) {
    return true;
  }

  const isDragEvent = /dragstart|dragover|dragend|drop/.test(event.type);
  if (isDragEvent) {
    return false;
  }

  const isClipboardEvent = /copy|paste|cut/.test(event.type);
  if (isClipboardEvent) {
    return false;
  }

  const isKeyboardEvent = /keydown|keyup|keypress/.test(event.type);
  if (isKeyboardEvent) {
    return false;
  }

  const isCompositionEvent = /compositionstart|compositionend|compositionupdate|beforeinput/.test(event.type);
  if (isCompositionEvent) {
    return false;
  }

  return !/mousedown|drag|drop/.test(event.type);
}

function isFormInputElement(target: EventTarget | null): boolean {
  const tagName = (target as HTMLElement | null)?.tagName;
  return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
}
